import React, { useMemo, useState } from 'react';
import NavBar from "../nav-bar/Nav";
import './integrations.scss';


const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'cloud', label: 'Infrastructure' },
  { id: 'smb', label: 'SMB' },
  { id: 'msp', label: 'MSP & operations' },
];

const INTEGRATIONS = [
  {
    id: 'azure',
    name: 'Microsoft Azure',
    category: 'cloud',
    tag: 'Cloud platform',
    initials: 'Az',
    logo: 'azure.png',
    summary:
      'Run Level 2 assessments against your Azure environment to measure cloud security posture',
    points: [
      'Security posture assessment across subscriptions and resources',
      'Findings feed into loss scenario modeling',
      'Streamlined vendor onboarding and insurance applicant workflows',
    ],
    badge: 'Internal assessment', 
  },
  {
    id: 'aws',
    name: 'Amazon Web Services',
    category: 'cloud',
    tag: 'Cloud platform',
    initials: 'AWS',
    logo: 'aws.svg',
    summary:
      'Assess account configurations using AWS best practice recommendations.',
    points: [
      'Uniform set of controls based on well documented best practices',
      'Automated assessment that spans across all AWS services',
      'Findings map into type of risk exposure',
    ],
    badge: 'Internal assessment',
  },
  {
    id: 'm365',
    name: 'Microsoft 365',
    category: 'smb',
    tag: 'SMB assessment',
    initials: 'M365',
    logo: 'm365.svg',
    summary:
      'M365 environment assessments that are based on a simple set of basic controls.',
    points: [
      'Tenant security configuration review',
      'Assess actual implementation of controls',
      'Client self-onboarding via 3-click workflow',
    ],
    badge: 'Tenant assessment',
  },
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    category: 'smb',
    tag: 'SMB assessment',
    initials: 'GW',
    logo: 'google.png',
    summary:
      'Cover organizations that run on Google Workspace with the same approach as M36 environments.',
    points: [
      'Tenant security configuration review',
      'Assess actual implementation of controls',
      'Client self-onboarding via 3-click workflow',
    ],
    badge: 'Tenant assessment',
  },
  {
    id: 'scalepad',
    name: 'ScalePad',
    category: 'msp',
    tag: 'MSP & operations',
    initials: 'SP',
    logo: 'scalepad.png',
    summary:
      'Give managed service providers a way to improve client engagement with cyber risk assesments and exposure data',
    points: [
      'Align SignetRisk assessments with client records',
      'Support business reviews and planning with control findings',
      'Help clients justify security investment and insurance decisions',
    ],
    badge: 'MSP workflow',
  },
  {
    id: 'halo',
    name: 'Halo',
    category: 'msp',
    tag: 'MSP & operations',
    initials: 'Ha',
    logo: 'halo.svg',
    summary:
      'Connect risk findings to service management. Halo gives your team a place to turn assessment results into tracked, assigned remediation work.',
    points: [
      'Route high-impact findings into ticketing',
      'Track remediation via Halo projects',
      'Expand sales and prospecting funnel with look-alike peer data',
    ],
    badge: 'MSP workflow', 
  },
  {
    id: 'liongard',
    name: 'Liongard',
    category: 'msp',
    tag: 'MSP & operations',
    initials: 'Lg',
    logo: 'liongard.png',
    summary:
      'Increase client growth with better data and analytics on the first meeting.  HunterX allows your sales team to quickly demonstrate an understanding of the client, their environment, and their exposures',
    points: [
      'Identify targets to grow the prospecting funnel',
      'Incorporate summary reports of cyber risk profile in client conversations',
      'Bring initial tech stack and external assessent to the first meeting.',
    ],
    badge: 'MSP sales',

  },
];

const STEPS = [
  {
    title: 'Connect',
    body: 'Authorize read-only access to the platforms you want assessed. SignetRisk does the rest.',
  },
  {
    title: 'Quantify',
    body: 'SignetRisk turns control findings data into business outcome impacts via financial loss scenarios.',
  },
  {
    title: 'Act and report',
    body: 'Send control findings into your remediation workflows. Identify look-alike peers for sales growth.',
  },
];

const FAQS = [
  {
    q: 'Do integrations require a Level 2 assessment?',
    a: 'Level 1 assessments use SignetScore and do not need asset mapping since they look at the external view of the company. This makes them quick to deploy across a portfolio. Internal data integrations support deeper Level 2 assessments.',
  },
  {
    q: 'What access does SignetRisk need?',
    a: 'We request the minimum access required to evaluate security posture. Generally read level access to various control findings or configurations.',
  },
  {
    q: 'Can I use SignetRisk across multiple clients or vendors?',
    a: 'Yes. Assessments can be combined across organizations to reveal shared technology dependencies and concentration risk.',
  },
  {
    q: 'Can you build an integration we need?',
    a: 'Tell us what you run. We prioritize new integrations based on customer demand.',
  },
];

const NAV_LINKS = [
  { label: 'Solutions', href: '/solutions' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/faq' },
  { label: 'About', href: '/about' },
];

/** Partner logo with an initials fallback until (or unless) the image loads. */
function LogoTile({ src, alt, initials }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'loaded' | 'error'

  return (
    <div className={`integrations__mark${status === 'loaded' ? ' is-logo' : ''}`}>
      {status !== 'error' && (
        <img
          src={src}
          alt={alt}
          width="32"
          height="32"
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      )}
      {status !== 'loaded' && <span aria-hidden="true">{initials}</span>}
    </div>
  );
}

function SiteNav({ signupHref }) {
  return (
    <nav className="integrations__nav" aria-label="Main">
      <a className="integrations__brand" href="/" aria-label="SignetRisk Analytics home">
        <svg
          viewBox="0 0 40 30"
          fill="none"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M10 5 3 15l7 10M30 5l7 10-7 10M23 3 17 27" />
        </svg>
        <span>
          SignetRisk
          <br />
          Analytics
        </span>
      </a>
      <div className="integrations__links">
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={l.href}>
            {l.label}
          </a>
        ))}
      </div>
      <div className="integrations__nav-right">
        <a className="integrations__login" href="/login">
          Login
        </a>
        <a className="integrations__btn" href={signupHref}>
          Signup
        </a>
      </div>
    </nav>
  );
}

export default function Integrations({
  showNav = true,
  logoBasePath = `${process.env.PUBLIC_URL || ''}/assets`,
  signupHref = 'https://app.signetrisk.com/signup',
}) {
  const [category, setCategory] = useState('all');

  const visible = useMemo(
    () => INTEGRATIONS.filter((i) => category === 'all' || i.category === category),
    [category]
  );

  return (
    <main className="integrations">
      <NavBar />
      {/* Hero */}
      <header className="integrations__hero">
        <div className="integrations__wrap">
          {/*showNav && <SiteNav signupHref={signupHref} />*/}

          <div className="integrations__hero-body">
            <span className="integrations__eyebrow">Integrations</span>
            <h1>Cyber risk findings, connected to the tools you already run</h1>
            <p className="integrations__lede">
              Bring internal and external cyber risk findings into the platforms behind your
              managed accounts, so every client conversation starts from real data and ongoing
              value is easy to show.
            </p>
            <a className="integrations__btn" href={signupHref}>
              Get started
            </a>
            <a className="integrations__btn integrations__btn--ghost" href="#catalog">
              Browse integrations
            </a>
          </div>
        </div>
      </header>

      {/* Catalog */}
      <section id="catalog" className="integrations__section integrations__section--catalog">
        <div className="integrations__wrap">
          <div className="integrations__filters" role="group" aria-label="Filter integrations by category">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className="integrations__chip"
                aria-pressed={category === c.id}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="integrations__grid">
            {visible.map((item) => (
              <article className="integrations__card" key={item.id}>
                <div className="integrations__card-top">
                  <LogoTile
                    src={`${logoBasePath}/${item.logo}`}
                    alt={`${item.name} logo`}
                    initials={item.initials}
                  />
                  <div>
                    <h3>{item.name}</h3>
                    <span className="integrations__tag">{item.tag}</span>
                  </div>
                </div>
                <p>{item.summary}</p>
                <ul>
                  {item.points.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
                <span className="integrations__badge">{item.badge}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="integrations__section integrations__section--alt">
        <div className="integrations__wrap">
          <h2>How integrations work</h2>
          <p className="integrations__sub">
            Connect once, assess continuously, and put the result where decisions get made.
          </p>
          <div className="integrations__steps">
            {STEPS.map((s) => (
              <div className="integrations__step" key={s.title}>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="integrations__section">
        <div className="integrations__wrap">
          <h2>Frequently asked questions</h2>
          <p className="integrations__sub">Not seeing your tool? Ask us.</p>
          <div className="integrations__faq">
            {FAQS.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="integrations__cta">
        <div className="integrations__wrap">
          <h2>Put your risk data to work</h2>
          <p>
            See how SignetRisk fits into your environment and what your exposure looks like in
            financial terms.
          </p>
          <a className="integrations__btn" href={signupHref}>
            Get started
          </a>
        </div>
      </div>
    </main>
  );
}
